import {
  closestCenter,
  closestCorners,
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { memo, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { Actor, Bucket, CreateItem, Item, Status, StatusCategory } from "../api/types";
import { AvatarStack, DueChip, LabelChips, PriorityMarker, SyncBadges } from "../components/bits";
import { QuickAdd } from "../components/QuickAdd";
import { MenuButton } from "../components/ContextMenu";
import { useItemMenu } from "../components/ItemMenu";
import { keyBetween } from "../lib/format";
import {
  cardDrop,
  COL_PREFIX,
  containerOf,
  dragOverColumns,
  isBoardItem,
  neighbours,
  type Columns,
} from "../lib/kanban";
import { moveItem, toastError } from "../store/app";
import { useActorMap, useActors } from "../store/hooks";

/** Per-card decorations supplied by the board's owner (done checkbox, collection chip). */
interface CardDecor {
  lead?: (item: Item) => ReactNode;
  extra?: (item: Item) => ReactNode;
}

const CardBody = memo(function CardBody({
  item,
  actors,
  childCount,
  lead,
  extra,
}: {
  item: Item;
  actors: Map<string, Actor>;
  childCount: number;
  lead?: ReactNode;
  extra?: ReactNode;
}) {
  const location = useLocation();
  return (
    <>
      <div className="card-title-row">
        {lead}
        <PriorityMarker priority={item.priority} />
        <Link
          className="card-title"
          to={`/items/${item.id}`}
          state={{ background: location }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {item.title || "(untitled)"}
        </Link>
      </div>
      <div className="card-meta">
        {extra}
        <DueChip item={item} />
        <LabelChips labels={item.labels} />
        {childCount > 0 && <span className="chip" title="Subtasks">☰ {childCount}</span>}
        <span className="spacer" />
        <SyncBadges sync={item.sync} />
        <AvatarStack ids={item.assignees} actors={actors} />
      </div>
    </>
  );
});

function SortableCard({
  item,
  actors,
  childCount,
  decor,
}: {
  item: Item;
  actors: Map<string, Actor>;
  childCount: number;
  decor: CardDecor;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const menu = useItemMenu(item);
  return (
    <div
      ref={setNodeRef}
      className={`card${isDragging ? " dragging" : ""}${item.status_category === "done" ? " done" : ""}${menu.isOpen ? " menu-open" : ""}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
      // Right-click and Shift+F10 open the item menu; other keys still drive keyboard dragging.
      onContextMenu={menu.targetProps.onContextMenu}
      onKeyDown={(e) => {
        menu.targetProps.onKeyDown(e);
        if (!e.defaultPrevented) listeners?.onKeyDown?.(e);
      }}
      aria-roledescription="Draggable card. Press space to pick up, arrows to move, space to drop."
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("a, input, button")) return;
        navigate(`/items/${item.id}`, { state: { background: location } });
      }}
    >
      <CardBody
        item={item}
        actors={actors}
        childCount={childCount}
        lead={decor.lead?.(item)}
        extra={decor.extra?.(item)}
      />
      <MenuButton
        label={`${item.title || "(untitled)"} actions`}
        expanded={menu.isOpen}
        onOpen={menu.openFrom}
        className="card-more"
      />
      {menu.menu}
    </div>
  );
}

export interface BoardColumnDef {
  key: string;
  title: string;
  category?: StatusCategory;
  color?: string | null;
  /** Columns can be dragged to reorder (bucket board). */
  sortable?: boolean;
  /** Custom header content (replaces dot + title + count). */
  renderHead?: (count: number) => ReactNode;
  /** Status / bucket defaults for "+ Add card" in this column. */
  defaultStatusId?: string;
}

function BoardColumn({
  def,
  ids,
  items,
  actors,
  actorList,
  childCounts,
  decor,
  onCreate,
  quickAdd,
}: {
  def: BoardColumnDef;
  ids: string[];
  items: Record<string, Item>;
  actors: Map<string, Actor>;
  actorList: Actor[];
  childCounts: Map<string, number>;
  decor: CardDecor;
  onCreate?: (columnKey: string, body: CreateItem) => Promise<unknown>;
  quickAdd?: { statuses?: Status[]; buckets?: Bucket[] };
}) {
  // Every column is a sortable (= droppable) node; only bucket columns can actually be dragged.
  const { setNodeRef, setActivatorNodeRef, attributes, listeners, transform, transition, isDragging, isOver } =
    useSortable({
      id: `${COL_PREFIX}${def.key}`,
      data: { type: "column" },
      disabled: { draggable: !def.sortable, droppable: false },
    });
  const [adding, setAdding] = useState(false);
  const dragProps = def.sortable ? { ...attributes, ...listeners, "aria-roledescription": "Draggable column" } : {};
  return (
    <section
      ref={setNodeRef}
      className={`column${isOver ? " over" : ""}${isDragging ? " dragging" : ""}`}
      aria-label={def.title}
      style={def.sortable ? { transform: CSS.Translate.toString(transform), transition } : undefined}
    >
      <header
        className={`column-head${def.sortable ? " draggable" : ""}`}
        ref={def.sortable ? setActivatorNodeRef : undefined}
        {...dragProps}
      >
        {def.renderHead ? (
          def.renderHead(ids.length)
        ) : (
          <>
            <span
              className={`cat-dot cat-${def.category ?? "todo"}`}
              style={def.color ? { background: def.color } : undefined}
            />
            <h2>{def.title}</h2>
            <span className="count">{ids.length}</span>
          </>
        )}
      </header>
      <SortableContext id={def.key} items={ids} strategy={verticalListSortingStrategy}>
        <div className="column-body">
          {ids.map((id) =>
            items[id] ? (
              <SortableCard key={id} item={items[id]} actors={actors} childCount={childCounts.get(id) ?? 0} decor={decor} />
            ) : null,
          )}
        </div>
      </SortableContext>
      {onCreate &&
        (adding ? (
          <div className="column-add">
            <QuickAdd
              compact
              statuses={quickAdd?.statuses}
              buckets={quickAdd?.buckets}
              actors={actorList}
              defaultStatusId={def.defaultStatusId}
              placeholder="Title @person #label due:fri"
              onCreate={(body) => onCreate(def.key, body)}
            />
            <button type="button" className="btn-link small" onClick={() => setAdding(false)}>
              Done
            </button>
          </div>
        ) : (
          <button type="button" className="column-add-btn" onClick={() => setAdding(true)}>
            + Add card
          </button>
        ))}
    </section>
  );
}

export interface CardMove {
  afterId: string | null;
  beforeId: string | null;
  /** Optimistic position between the neighbours. */
  position: string;
}

/**
 * Generic kanban board: columns of sortable cards (dnd-kit), optional column reordering and
 * "+ Add card" quick-add. The owner maps column keys to API moves.
 */
export function Board({
  columns,
  cards,
  items,
  onMoveCard,
  canBeSibling,
  onReorderColumn,
  onCreate,
  quickAdd,
  cardLead,
  cardExtra,
  trailing,
}: {
  columns: BoardColumnDef[];
  /** Card ids per column key (server order). */
  cards: Columns;
  items: Item[];
  onMoveCard: (id: string, columnKey: string, move: CardMove) => void;
  /** Whether a card can be the move-API neighbour of the dragged one (default: any). */
  canBeSibling?: (dragged: Item, other: Item) => boolean;
  onReorderColumn?: (activeKey: string, overKey: string) => void;
  onCreate?: (columnKey: string, body: CreateItem) => Promise<unknown>;
  quickAdd?: { statuses?: Status[]; buckets?: Bucket[] };
  cardLead?: (item: Item) => ReactNode;
  cardExtra?: (item: Item) => ReactNode;
  /** Rendered after the last column (e.g. "+ Add bucket"). */
  trailing?: ReactNode;
}) {
  const actors = useActorMap();
  const actorList = useActors();
  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const childCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of items) if (i.parent_id) m.set(i.parent_id, (m.get(i.parent_id) ?? 0) + 1);
    return m;
  }, [items]);
  const decor = useMemo<CardDecor>(() => ({ lead: cardLead, extra: cardExtra }), [cardLead, cardExtra]);
  const [dragColumns, setDragColumns] = useState<Columns | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [origin, setOrigin] = useState<{ container: string; index: number } | null>(null);
  const shown = dragColumns ?? cards;
  const columnIds = useMemo(() => columns.map((c) => `${COL_PREFIX}${c.key}`), [columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Columns only collide with columns; cards with everything (as before).
  const collision: CollisionDetection = (args) => {
    if (args.active.data.current?.type === "column") {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter((c) => String(c.id).startsWith(COL_PREFIX)),
      });
    }
    return closestCorners(args);
  };

  const onDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    setActiveId(id);
    if (id.startsWith(COL_PREFIX)) return;
    const container = containerOf(id, cards);
    setDragColumns(cards);
    if (container) setOrigin({ container, index: cards[container].indexOf(id) });
  };

  const onDragOver = (e: DragOverEvent) => {
    const { active, over } = e;
    if (!over || String(active.id).startsWith(COL_PREFIX)) return;
    setDragColumns((prev) => dragOverColumns(prev ?? cards, String(active.id), String(over.id)) ?? prev);
  };

  const finish = () => {
    setActiveId(null);
    setDragColumns(null);
    setOrigin(null);
  };

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    const id = String(active.id);
    if (id.startsWith(COL_PREFIX)) {
      const overKey = over ? containerOf(String(over.id), cards) : undefined;
      finish();
      if (overKey && onReorderColumn) onReorderColumn(id.slice(COL_PREFIX.length), overKey);
      return;
    }
    if (!over || !origin) return finish();
    const drop = cardDrop(dragColumns ?? cards, id, String(over.id), origin);
    if (!drop) return finish();
    const dragged = itemMap[id];
    const { afterId, beforeId } = neighbours(drop.list, drop.index, (x) =>
      !canBeSibling || !dragged || !itemMap[x] ? true : canBeSibling(dragged, itemMap[x]),
    );
    const position = keyBetween(
      afterId ? itemMap[afterId]?.position ?? null : null,
      beforeId ? itemMap[beforeId]?.position ?? null : null,
    );
    // The owner applies the optimistic update synchronously, so the card does not flicker back.
    onMoveCard(id, drop.container, { afterId, beforeId, position });
    finish();
  };

  const activeItem = activeId ? itemMap[activeId] : undefined;
  const activeColumn = activeId?.startsWith(COL_PREFIX)
    ? columns.find((c) => `${COL_PREFIX}${c.key}` === activeId)
    : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collision}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={finish}
    >
      <div className="board">
        <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
          {columns.map((c) => (
            <BoardColumn
              key={c.key}
              def={c}
              ids={shown[c.key] ?? []}
              items={itemMap}
              actors={actors}
              actorList={actorList}
              childCounts={childCounts}
              decor={decor}
              onCreate={onCreate}
              quickAdd={quickAdd}
            />
          ))}
        </SortableContext>
        {trailing}
      </div>
      <DragOverlay>
        {activeItem ? (
          <div className="card overlay">
            <CardBody
              item={activeItem}
              actors={actors}
              childCount={childCounts.get(activeItem.id) ?? 0}
              lead={cardLead?.(activeItem)}
              extra={cardExtra?.(activeItem)}
            />
          </div>
        ) : activeColumn ? (
          <div className="column overlay">
            <header className="column-head">
              <h2>{activeColumn.title}</h2>
              <span className="count">{(cards[activeColumn.key] ?? []).length}</span>
            </header>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/** Status columns of a collection. */
function statusColumns(statuses: Status[], items: Item[]): Columns {
  const cols: Columns = {};
  for (const s of statuses) cols[s.id] = [];
  for (const it of items) if (isBoardItem(it)) (cols[it.status_id] ??= []).push(it.id);
  return cols;
}

export function BoardView({
  statuses,
  items,
  onCreate,
}: {
  collectionId: string;
  statuses: Status[];
  items: Item[];
  onCreate: (body: CreateItem) => Promise<unknown>;
}) {
  const cards = useMemo(() => statusColumns(statuses, items), [statuses, items]);
  const columns = useMemo<BoardColumnDef[]>(
    () => statuses.map((s) => ({ key: s.id, title: s.name, category: s.category, color: s.color, defaultStatusId: s.id })),
    [statuses],
  );

  if (statuses.length === 0) return <p className="muted">This collection has no statuses.</p>;

  return (
    <Board
      columns={columns}
      cards={cards}
      items={items}
      onCreate={(key, body) => onCreate({ ...body, status_id: body.status_id ?? key })}
      onMoveCard={(id, key, m) => {
        const status = statuses.find((s) => s.id === key);
        if (!status) return;
        moveItem(id, {
          status_id: status.id,
          status_category: status.category,
          after_id: m.afterId,
          before_id: m.beforeId,
          position: m.position,
        }).catch((err) => toastError(err, "Move failed"));
      }}
    />
  );
}
